import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
  confirmReceipt,
  getReceiptStats,
  ReceiptApiError,
  ReceiptStats,
  ScanResult,
  scanReceipt,
} from '../../../utils/receiptApi';

// Hidden page (no nav entry): reached via the secret tap gesture in the
// sidebar. Photograph a receipt from the festival stand, the backend
// verifies it, then the visitor assigns its euro total as points to a
// borgo. 1 EUR = 1 point.

type Phase = 'idle' | 'scanning' | 'choose' | 'confirming' | 'done';

const ScontriniPage: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [stats, setStats] = useState<ReceiptStats | null>(null);
  const [lastBorgo, setLastBorgo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    getReceiptStats().then(setStats).catch(() => {});
  }, []);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setError(null);
    setPhase('scanning');
    try {
      setScan(await scanReceipt(file));
      setPhase('choose');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore imprevisto.');
      setPhase('idle');
    }
  };

  const handleBorgo = async (borgo: string) => {
    if (!scan) return;
    setError(null);
    setPhase('confirming');
    try {
      const result = await confirmReceipt(scan.token, borgo);
      setStats(result.stats);
      setLastBorgo(borgo);
      setPhase('done');
    } catch (err) {
      if (err instanceof ReceiptApiError && err.code === 'expired') {
        setError(err.message);
        setPhase('idle');
      } else {
        setError(err instanceof Error ? err.message : 'Errore imprevisto.');
        setPhase('choose');
      }
    }
  };

  const reset = () => {
    setScan(null);
    setError(null);
    setPhase('idle');
  };

  const sortedTotals = Object.entries(stats?.totals ?? {}).sort(
    (a, b) => b[1] - a[1],
  );

  return (
    <Box sx={{ p: 2, maxWidth: 480, mx: 'auto' }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{ fontFamily: "'Cinzel', serif", mb: 0.5 }}
      >
        La Taverna
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        Fotografa lo scontrino del chiosco e assegna i punti al tuo borgo.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {phase === 'idle' && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleFile}
          />
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<PhotoCameraIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ py: 1.5 }}
          >
            Fotografa lo scontrino
          </Button>
        </>
      )}

      {(phase === 'scanning' || phase === 'confirming') && (
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <CircularProgress />
          <Typography sx={{ color: 'text.secondary' }}>
            {phase === 'scanning'
              ? 'Lettura dello scontrino…'
              : 'Registrazione…'}
          </Typography>
        </Stack>
      )}

      {phase === 'choose' && scan && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h3" sx={{ textAlign: 'center', mb: 1 }}>
              {scan.summary.total_eur.toFixed(2).replace('.', ',')} €
            </Typography>
            <Typography
              sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}
            >
              Scontrino n. {scan.summary.doc_number} — {scan.summary.date}
              {scan.summary.time ? ` ${scan.summary.time}` : ''}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ mb: 1.5, fontWeight: 600 }}>
              Per quale borgo?
            </Typography>
            <Stack spacing={1}>
              {scan.villages.map((v) => (
                <Button key={v} variant="outlined" onClick={() => handleBorgo(v)}>
                  {v}
                </Button>
              ))}
            </Stack>
            <Button fullWidth sx={{ mt: 2 }} color="inherit" onClick={reset}>
              Annulla
            </Button>
          </CardContent>
        </Card>
      )}

      {phase === 'done' && scan && lastBorgo && (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            {Math.round(scan.summary.total_eur)} punti per {lastBorgo}!
          </Alert>
          <Button fullWidth variant="contained" onClick={reset}>
            Scansiona un altro
          </Button>
        </>
      )}

      {sortedTotals.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Classifica</Typography>
          {sortedTotals.map(([borgo, points]) => (
            <Box
              key={borgo}
              sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}
            >
              <Typography>{borgo}</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {Math.round(points)} punti
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ScontriniPage;
